import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Loader2, Download, ChevronDown, ChevronUp, RefreshCw,
  CheckCircle2, XCircle, AlertTriangle, BarChart3, Clock, Target,
  ExternalLink, Play,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import apiClient from '@/lib/apiClient';
import { escapeCsvCell } from '@/lib/dashboardUtils';

const GRADE_COLORS = {
  PASS: 'bg-green-100 text-green-800 border-green-300',
  SOFT_PASS: 'bg-sky-100 text-sky-800 border-sky-300',
  SOFT_FAIL: 'bg-yellow-100 text-yellow-800 border-yellow-300',
  FAIL: 'bg-red-100 text-red-800 border-red-300',
  ERROR: 'bg-gray-100 text-gray-800 border-gray-300',
  PARSE_ERROR: 'bg-gray-100 text-gray-800 border-gray-300',
};

const DIMENSION_LABELS = {
  factual_accuracy: 'Faktentreue',
  completeness: 'Vollständigkeit',
  citizen_friendliness: 'Bürgerfreundlichkeit',
  source_grounding: 'Quellenverankerung',
  intent_alignment: 'Intent-Ausrichtung',
  link_quality: 'Link-Qualität',
};

function dimScoreColor(score) {
  if (score >= 4) return 'text-green-600';
  if (score === 3) return 'text-yellow-600';
  return 'text-red-600';
}

function passRateColor(rate) {
  if (rate >= 0.8) return 'text-green-600';
  if (rate >= 0.6) return 'text-yellow-600';
  return 'text-red-600';
}

function passRateBg(rate) {
  if (rate >= 0.8) return 'bg-green-500';
  if (rate >= 0.6) return 'bg-yellow-500';
  return 'bg-red-500';
}

export default function QualitaetTab({ isDark, userInfo, selectedMunicipalityId, municipalitiesList }) {
  // Report data
  const [report, setReport] = useState(null);
  const [judgeReport, setJudgeReport] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Available reports
  const [availableReports, setAvailableReports] = useState([]);
  const [reportsLoading, setReportsLoading] = useState(false);

  // Results table state
  const [filterMode, setFilterMode] = useState('all');
  const [expandedRows, setExpandedRows] = useState({});
  const [judgeSectionOpen, setJudgeSectionOpen] = useState(true);
  const [judgeVerdictExpanded, setJudgeVerdictExpanded] = useState({});

  // Quality loop controls
  const [suite, setSuite] = useState('golden_v3');
  const [runJudge, setRunJudge] = useState(false);
  const [runCurate, setRunCurate] = useState(false);
  const [runEvolve, setRunEvolve] = useState(false);
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const [jobRunning, setJobRunning] = useState(false);
  const pollRef = useRef(null);

  const fetchReports = useCallback(async () => {
    try {
      setReportsLoading(true);
      const params = {};
      if (selectedMunicipalityId) params.municipality_id = selectedMunicipalityId;
      const res = await apiClient.get('/provider/quality/reports', { params });
      setAvailableReports(res.data || []);
    } catch (err) {
      console.error('Failed to load quality reports:', err);
    } finally {
      setReportsLoading(false);
    }
  }, []);

  const loadReport = useCallback(async (filename) => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiClient.get(`/provider/quality/reports/${filename}`);
      setReport(res.data);
      setExpandedRows({});

      const ts = filename.replace('eval_', '').replace('.json', '');
      const tsParts = ts.split('_').slice(0, 2).join('_');
      const judgeFile = availableReports.find(
        (r) => r.type === 'judge' && r.timestamp === tsParts
      );
      if (judgeFile) {
        try {
          const jr = await apiClient.get(`/provider/quality/reports/${judgeFile.filename_json}`);
          setJudgeReport(jr.data);
        } catch {
          setJudgeReport(null);
        }
      } else {
        setJudgeReport(null);
      }
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      toast.error('Report konnte nicht geladen werden');
    } finally {
      setLoading(false);
    }
  }, [availableReports]);

  useEffect(() => {
    if (userInfo?.role === 'ProviderAdmin') {
      setReport(null);
      setJudgeReport(null);
      fetchReports();
    }
  }, [userInfo, fetchReports, selectedMunicipalityId]);

  // Auto-load latest eval report
  useEffect(() => {
    if (!report && availableReports.length > 0) {
      const latestEval = availableReports.find((r) => r.type === 'eval');
      if (latestEval) {
        loadReport(latestEval.filename_json);
      }
    }
  }, [availableReports, report, loadReport]);

  // Poll job status
  useEffect(() => {
    if (!jobId || !jobRunning) return;
    pollRef.current = setInterval(async () => {
      try {
        const res = await apiClient.get(`/provider/quality/run/${jobId}`);
        setJobStatus(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed' || res.data.status === 'cancelled') {
          setJobRunning(false);
          clearInterval(pollRef.current);
          if (res.data.status === 'completed') {
            toast.success('Quality Loop abgeschlossen');
            fetchReports();
            if (res.data.report_paths?.eval_json) {
              loadReport(res.data.report_paths.eval_json);
            }
          } else if (res.data.status === 'failed') {
            toast.error(`Quality Loop fehlgeschlagen: ${res.data.error}`);
          }
        }
      } catch (err) {
        console.error('Poll error:', err);
      }
    }, 3000);
    return () => clearInterval(pollRef.current);
  }, [jobId, jobRunning, fetchReports, loadReport]);

  const startQualityLoop = async () => {
    if (!selectedMunicipalityId) {
      toast.error('Bitte wählen Sie zuerst einen Mandanten aus');
      return;
    }
    try {
      setJobRunning(true);
      setJobStatus(null);
      const res = await apiClient.post('/provider/quality/run', {
        municipality_id: selectedMunicipalityId,
        suite,
        run_judge: runJudge,
        run_curate: runCurate,
        run_evolve: runEvolve,
      });
      setJobId(res.data.job_id);
      toast.info('Quality Loop gestartet...');
    } catch (err) {
      setJobRunning(false);
      toast.error(err.response?.data?.detail || 'Fehler beim Starten');
    }
  };

  const downloadReport = async (filename) => {
    try {
      const res = await apiClient.get(`/provider/quality/reports/${filename}`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${filename} heruntergeladen`);
    } catch (err) {
      toast.error('Download fehlgeschlagen');
    }
  };

  const exportResultsCsv = () => {
    if (!report?.results) return;
    const rows = [
      ['Status', 'Test ID', 'Question', 'Expected Intent', 'Actual Intent', 'Confidence', 'Latency (ms)', 'Answer'].map(escapeCsvCell),
    ];
    for (const r of report.results) {
      rows.push([
        r.overall_passed || r.passed ? 'PASS' : 'FAIL',
        r.test_id || '',
        r.question || '',
        r.expected_intent || '',
        r.actual_intent || r.normalized_intent || '',
        r.confidence ?? '',
        r.latency_ms ?? '',
        r.answer || '',
      ].map(escapeCsvCell));
    }
    const csv = rows.map((r) => r.join(',')).join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `quality_results_${new Date().toISOString().slice(0, 10)}.csv`;
    link.click();
    URL.revokeObjectURL(url);
    toast.success('CSV exportiert');
  };

  const summary = report?.summary || {};
  const results = report?.results || [];
  const filteredResults = filterMode === 'failures'
    ? results.filter((r) => !(r.overall_passed || r.passed))
    : results;

  const cardCls = isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200';
  const textCls = isDark ? 'text-white' : 'text-slate-900';
  const mutedCls = isDark ? 'text-slate-400' : 'text-slate-500';
  const headerBg = isDark ? 'bg-slate-950' : 'bg-slate-50';
  const rowBorder = isDark ? 'border-slate-800' : 'border-slate-200';

  return (
    <>
      <h2 className={`text-lg font-semibold ${textCls}`}>Qualitätssicherung</h2>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* ===== Section 1: KPI Hero Cards ===== */}
      {report && (
        <div className="space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Pass Rate */}
            <Card className={cardCls}>
              <CardHeader className="pb-2">
                <CardDescription className={mutedCls}>Pass Rate</CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${passRateColor(summary.pass_rate || 0)}`}>
                  {((summary.pass_rate || 0) * 100).toFixed(1)}%
                </div>
                <p className={`text-sm mt-1 ${mutedCls}`}>
                  {summary.passed || 0}/{summary.total_tests || 0} bestanden
                </p>
              </CardContent>
            </Card>

            {/* Intent Accuracy */}
            <Card className={cardCls}>
              <CardHeader className="pb-2">
                <CardDescription className={mutedCls}>Intent Accuracy</CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${passRateColor(summary.intent_accuracy || 0)}`}>
                  {((summary.intent_accuracy || 0) * 100).toFixed(1)}%
                </div>
                <p className={`text-sm mt-1 ${mutedCls}`}>
                  <Target className="inline w-3 h-3 mr-1" />
                  Intent-Erkennung
                </p>
              </CardContent>
            </Card>

            {/* Avg Latency */}
            <Card className={cardCls}>
              <CardHeader className="pb-2">
                <CardDescription className={mutedCls}>Ø Latenz</CardDescription>
              </CardHeader>
              <CardContent>
                <div className={`text-3xl font-bold ${textCls}`}>
                  {Math.round(summary.avg_latency_ms || 0)} ms
                </div>
                <p className={`text-sm mt-1 ${mutedCls}`}>
                  <Clock className="inline w-3 h-3 mr-1" />
                  Durchschnittliche Antwortzeit
                </p>
              </CardContent>
            </Card>
          </div>

          {/* Pass/Fail progress bar */}
          <div className={`rounded-lg overflow-hidden h-2 ${isDark ? 'bg-slate-800' : 'bg-slate-200'}`}>
            <div
              className={passRateBg(summary.pass_rate || 0)}
              style={{ width: `${(summary.pass_rate || 0) * 100}%`, height: '100%', transition: 'width 0.5s ease' }}
            />
          </div>
        </div>
      )}

      {/* ===== Section 2: Question-by-Question Results ===== */}
      {report && (
        <Card className={cardCls}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className={textCls}>Testergebnisse</CardTitle>
                <CardDescription className={mutedCls}>
                  {filteredResults.length} von {results.length} Tests
                  {report.timestamp && ` — ${report.timestamp}`}
                </CardDescription>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant={filterMode === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterMode('all')}
                  className={filterMode === 'all' ? 'bg-sky-600 text-white' : ''}
                >
                  Alle
                </Button>
                <Button
                  variant={filterMode === 'failures' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setFilterMode('failures')}
                  className={filterMode === 'failures' ? 'bg-red-600 text-white' : ''}
                >
                  Nur Fehler
                </Button>
                <Button variant="outline" size="sm" onClick={exportResultsCsv} title="CSV Export">
                  <Download className="w-4 h-4 mr-1" /> CSV
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-auto" style={{ maxHeight: 'calc(100vh - 320px)' }}>
              <Table>
                <TableHeader className={headerBg}>
                  <TableRow>
                    <TableHead className="w-[80px]">Status</TableHead>
                    <TableHead className="w-[100px]">Test ID</TableHead>
                    <TableHead>Frage</TableHead>
                    <TableHead className="w-[200px]">Intent</TableHead>
                    <TableHead className="w-[80px] text-right">Konfidenz</TableHead>
                    <TableHead className="w-[80px] text-right">Latenz</TableHead>
                    <TableHead className="w-[40px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredResults.map((r) => {
                    const passed = r.overall_passed || r.passed;
                    const isExpanded = expandedRows[r.test_id];
                    const intentMatch = r.intent_correct ?? (r.expected_intent === (r.normalized_intent || r.actual_intent));
                    const judgeVerdict = judgeReport?.verdicts?.find((v) => v.test_id === r.test_id);

                    return (
                      <React.Fragment key={r.test_id}>
                        <TableRow
                          className={`${rowBorder} cursor-pointer border-l-4 ${
                            passed ? 'border-l-green-500' : 'border-l-red-500'
                          } hover:${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}
                          onClick={() =>
                            setExpandedRows((prev) => ({ ...prev, [r.test_id]: !isExpanded }))
                          }
                        >
                          <TableCell>
                            <Badge className={passed
                              ? 'bg-green-100 text-green-800 border-green-300'
                              : 'bg-red-100 text-red-800 border-red-300'
                            }>
                              {passed ? 'PASS' : 'FAIL'}
                            </Badge>
                            {r.normalization_reason === 'soft_pass' && (
                              <Badge className="ml-1 bg-sky-100 text-sky-800 border-sky-300 text-xs">soft</Badge>
                            )}
                          </TableCell>
                          <TableCell className={`font-mono text-xs ${mutedCls}`}>{r.test_id}</TableCell>
                          <TableCell className={`text-sm ${textCls}`}>{r.question}</TableCell>
                          <TableCell>
                            <span className={`text-xs ${intentMatch ? 'text-green-600' : 'text-red-500'}`}>
                              {r.expected_intent}
                              {!intentMatch && ` → ${r.normalized_intent || r.actual_intent}`}
                            </span>
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {r.confidence != null ? (r.confidence * 100).toFixed(0) + '%' : '—'}
                          </TableCell>
                          <TableCell className="text-right text-sm">
                            {r.latency_ms != null ? `${r.latency_ms}ms` : '—'}
                          </TableCell>
                          <TableCell>
                            {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </TableCell>
                        </TableRow>

                        {isExpanded && (
                          <TableRow className={rowBorder}>
                            <TableCell colSpan={7} className="p-4">
                              <div className="space-y-3">
                                {/* Answer */}
                                <div>
                                  <Label className={mutedCls}>Antwort</Label>
                                  <div className={`mt-1 p-3 rounded-lg text-sm whitespace-pre-wrap ${
                                    isDark ? 'bg-slate-800 text-slate-200' : 'bg-slate-50 text-slate-800'
                                  }`}>
                                    {r.answer || '(keine Antwort)'}
                                  </div>
                                </div>

                                {/* Citations */}
                                {r.citations?.length > 0 && (
                                  <div>
                                    <Label className={mutedCls}>Quellen ({r.citations.length})</Label>
                                    <div className="mt-1 space-y-1">
                                      {r.citations.map((c, i) => (
                                        <a
                                          key={i}
                                          href={c.url}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="flex items-center gap-1 text-sm text-sky-500 hover:underline"
                                        >
                                          <ExternalLink className="w-3 h-3" />
                                          {c.title || c.url}
                                        </a>
                                      ))}
                                    </div>
                                  </div>
                                )}

                                {/* Failure reasons */}
                                {!passed && (
                                  <div>
                                    <Label className="text-red-500">Fehlerursachen</Label>
                                    <div className="mt-1 space-y-1">
                                      {(r.answer_eval?.reasons || r.fail_reasons || []).map((reason, i) => (
                                        <Badge key={i} variant="outline" className="bg-red-50 text-red-700 border-red-300 mr-1">
                                          {reason}
                                        </Badge>
                                      ))}
                                      {r.failure_bucket && (
                                        <Badge variant="outline" className="bg-red-50 text-red-700 border-red-300">
                                          Bucket: {r.failure_bucket}
                                        </Badge>
                                      )}
                                    </div>
                                  </div>
                                )}

                                {/* Soft-pass notes */}
                                {r.normalization_reason === 'soft_pass' && (
                                  <Badge className="bg-sky-50 text-sky-700 border-sky-300">
                                    Soft-Pass: Intent-Normalisierung akzeptiert
                                  </Badge>
                                )}

                                {/* Judge verdict (if available) */}
                                {judgeVerdict && (
                                  <div className={`p-3 rounded-lg border ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-amber-50 border-amber-200'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                      <BarChart3 className="w-4 h-4 text-amber-500" />
                                      <span className={`font-medium text-sm ${textCls}`}>LLM Judge Bewertung</span>
                                      <Badge className={GRADE_COLORS[judgeVerdict.overall_grade] || ''}>
                                        {judgeVerdict.overall_grade}
                                      </Badge>
                                    </div>
                                    <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
                                      {Object.entries(DIMENSION_LABELS).map(([dim, label]) => (
                                        <div key={dim} className="text-center">
                                          <div className={`text-lg font-bold ${dimScoreColor(judgeVerdict[dim] ?? 0)}`}>
                                            {judgeVerdict[dim] ?? '—'}
                                          </div>
                                          <div className={`text-xs ${mutedCls}`}>{label}</div>
                                        </div>
                                      ))}
                                    </div>
                                    {judgeVerdict.reasoning && (
                                      <p className={`mt-2 text-xs ${mutedCls}`}>{judgeVerdict.reasoning}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ===== Section 3: LLM Judge Summary ===== */}
      {judgeReport && (
        <Card className={cardCls}>
          <CardHeader
            className="cursor-pointer"
            onClick={() => setJudgeSectionOpen((v) => !v)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className={textCls}>
                  <BarChart3 className="inline w-5 h-5 mr-2 text-amber-500" />
                  LLM Judge Zusammenfassung
                </CardTitle>
                <CardDescription className={mutedCls}>
                  {judgeReport.total_judged} Fälle bewertet · Modell: {judgeReport.model}
                </CardDescription>
              </div>
              {judgeSectionOpen ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </CardHeader>

          {judgeSectionOpen && (
            <CardContent className="space-y-4">
              {/* Grade distribution */}
              <div>
                <Label className={mutedCls}>Notenverteilung</Label>
                <div className="flex flex-wrap gap-2 mt-2">
                  {['PASS', 'SOFT_PASS', 'SOFT_FAIL', 'FAIL'].map((grade) => {
                    const count = judgeReport.grade_distribution?.[grade] || 0;
                    return (
                      <Badge key={grade} className={`${GRADE_COLORS[grade]} text-sm px-3 py-1`}>
                        {grade}: {count}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* Average dimension scores */}
              {judgeReport.avg_dimension_scores && Object.keys(judgeReport.avg_dimension_scores).length > 0 && (
                <div>
                  <Label className={mutedCls}>Durchschnittliche Dimensionswerte</Label>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-4 mt-2">
                    {Object.entries(DIMENSION_LABELS).map(([dim, label]) => {
                      const score = judgeReport.avg_dimension_scores[dim];
                      return (
                        <div key={dim} className={`text-center p-2 rounded-lg ${isDark ? 'bg-slate-800' : 'bg-slate-50'}`}>
                          <div className={`text-xl font-bold ${dimScoreColor(score ?? 0)}`}>
                            {score != null ? score.toFixed(1) : '—'}
                          </div>
                          <div className={`text-xs mt-1 ${mutedCls}`}>{label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Test expectation bugs */}
              {judgeReport.test_expectation_bugs?.length > 0 && (
                <div>
                  <Label className="text-amber-600">Test-Erwartungsfehler ({judgeReport.test_expectation_bugs.length})</Label>
                  <div className="mt-2 space-y-1">
                    {judgeReport.test_expectation_bugs.map((bug, i) => (
                      <div key={i} className={`text-sm p-2 rounded ${isDark ? 'bg-amber-900/30 text-amber-200' : 'bg-amber-50 text-amber-800'}`}>
                        <span className="font-mono text-xs">{bug.test_id}</span>: {bug.bug}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <Separator className={isDark ? 'bg-slate-700' : ''} />

              {/* Individual verdicts */}
              <div>
                <Label className={mutedCls}>Einzelbewertungen</Label>
                <div className="mt-2 space-y-2">
                  {judgeReport.verdicts?.map((v) => {
                    const isOpen = judgeVerdictExpanded[v.test_id];
                    return (
                      <div key={v.test_id} className={`rounded-lg border ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                        <div
                          className={`flex items-center justify-between p-3 cursor-pointer ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}
                          onClick={() =>
                            setJudgeVerdictExpanded((prev) => ({ ...prev, [v.test_id]: !isOpen }))
                          }
                        >
                          <div className="flex items-center gap-2">
                            <Badge className={GRADE_COLORS[v.overall_grade] || ''}>
                              {v.overall_grade}
                            </Badge>
                            <span className={`font-mono text-xs ${mutedCls}`}>{v.test_id}</span>
                            <span className={`text-sm ${textCls}`}>{v.question?.substring(0, 60)}{v.question?.length > 60 ? '...' : ''}</span>
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="hidden md:flex items-center gap-1 text-xs">
                              {Object.entries(DIMENSION_LABELS).map(([dim]) => (
                                <span key={dim} className={`font-bold ${dimScoreColor(v[dim] ?? 0)}`}>
                                  {v[dim] ?? '—'}
                                </span>
                              ))}
                            </div>
                            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                          </div>
                        </div>

                        {isOpen && (
                          <div className={`p-3 border-t space-y-3 ${isDark ? 'border-slate-700' : 'border-slate-200'}`}>
                            {/* Dimension scores */}
                            <div className="grid grid-cols-3 md:grid-cols-6 gap-2 text-sm">
                              {Object.entries(DIMENSION_LABELS).map(([dim, label]) => (
                                <div key={dim} className="text-center">
                                  <div className={`text-lg font-bold ${dimScoreColor(v[dim] ?? 0)}`}>
                                    {v[dim] ?? '—'}/5
                                  </div>
                                  <div className={`text-xs ${mutedCls}`}>{label}</div>
                                </div>
                              ))}
                            </div>

                            {/* Reasoning */}
                            {v.reasoning && (
                              <div>
                                <Label className={mutedCls}>Begründung</Label>
                                <p className={`mt-1 text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`}>
                                  {v.reasoning}
                                </p>
                              </div>
                            )}

                            {/* Proposed golden answer */}
                            {v.proposed_golden_answer && (
                              <div>
                                <Label className="text-sky-600">Vorgeschlagene Musterantwort</Label>
                                <div className={`mt-1 p-3 rounded-lg text-sm whitespace-pre-wrap ${
                                  isDark ? 'bg-sky-950 text-sky-200 border border-sky-800' : 'bg-sky-50 text-sky-900 border border-sky-200'
                                }`}>
                                  {v.proposed_golden_answer}
                                </div>
                              </div>
                            )}

                            {/* Test expectation bug */}
                            {v.test_expectation_bug && (
                              <Badge className="bg-amber-100 text-amber-800 border-amber-300">
                                Test-Bug: {v.test_expectation_bug}
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </CardContent>
          )}
        </Card>
      )}

      {/* ===== Section 4: Quality Loop Controls ===== */}
      <Card className={cardCls}>
        <CardHeader>
          <CardTitle className={textCls}>Quality Loop Steuerung</CardTitle>
          <CardDescription className={mutedCls}>
            Eval-Suite und LLM Judge als Pipeline starten
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Suite selector */}
          <div className="space-y-2">
            <Label className={textCls}>Test-Suite</Label>
            <RadioGroup value={suite} onValueChange={setSuite} className="flex gap-4">
              {[
                { value: 'golden_v3', label: 'Golden v3 (20 Tests, verifiziert)' },
                { value: 'golden', label: 'Golden v1 (16 Tests)' },
                { value: 'golden_v2', label: 'Golden v2 (44 Tests)' },
                { value: 'smoke', label: 'Smoke (schnell)' },
                { value: 'full', label: 'Full (~960 Tests)' },
                { value: 'super_judge_300', label: 'Super-Judge 300 (4 Tiers)' },
              ].map((opt) => (
                <div key={opt.value} className="flex items-center space-x-2">
                  <RadioGroupItem value={opt.value} id={`suite-${opt.value}`} />
                  <Label htmlFor={`suite-${opt.value}`} className={`cursor-pointer ${textCls}`}>
                    {opt.label}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          <Separator className={isDark ? 'bg-slate-700' : ''} />

          {/* Switches */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center justify-between">
              <Label htmlFor="run-judge" className={textCls}>LLM Judge</Label>
              <Switch id="run-judge" checked={runJudge} onCheckedChange={setRunJudge} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="run-curate" className={textCls}>Auto-Kuratierung</Label>
              <Switch id="run-curate" checked={runCurate} onCheckedChange={setRunCurate} />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="run-evolve" className={textCls}>Evolution</Label>
              <Switch id="run-evolve" checked={runEvolve} onCheckedChange={setRunEvolve} />
            </div>
          </div>

          {/* Start button */}
          <Button
            onClick={startQualityLoop}
            disabled={jobRunning || !selectedMunicipalityId}
            className="w-full bg-sky-600 hover:bg-sky-700 text-white"
            size="lg"
          >
            {jobRunning ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Läuft...
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                Quality Loop starten
              </>
            )}
          </Button>

          {/* Job progress */}
          {jobStatus && (
            <div className={`p-4 rounded-lg border space-y-3 ${isDark ? 'bg-slate-800 border-slate-700' : 'bg-slate-50 border-slate-200'}`}>
              <div className="flex items-center justify-between text-sm">
                <span className={textCls}>
                  Phase: <span className="font-medium">{jobStatus.phase}</span>
                </span>
                <Badge variant="outline">{jobStatus.status}</Badge>
              </div>
              <Progress value={(jobStatus.progress || 0) * 100} className="h-2" />
              <div className={`flex justify-between text-xs ${mutedCls}`}>
                <span>{jobStatus.completed_tests}/{jobStatus.total_tests} Tests</span>
                <span>
                  <CheckCircle2 className="inline w-3 h-3 text-green-500 mr-1" />
                  {jobStatus.passed_tests}
                  <XCircle className="inline w-3 h-3 text-red-500 ml-2 mr-1" />
                  {jobStatus.failed_tests}
                </span>
              </div>
              {jobStatus.current_test && (
                <p className={`text-xs truncate ${mutedCls}`}>{jobStatus.current_test}</p>
              )}
              {jobStatus.error && (
                <Alert variant="destructive">
                  <AlertDescription className="text-xs">{jobStatus.error}</AlertDescription>
                </Alert>
              )}
              {jobStatus.curate_summary && (
                <div className={`text-xs p-2 rounded ${isDark ? 'bg-slate-700' : 'bg-amber-50'}`}>
                  <span className="font-medium">Kuratierung:</span>{' '}
                  {jobStatus.curate_summary.total_verdicts} Verdicts,{' '}
                  {jobStatus.curate_summary.auto_applicable_count} auto-anwendbar
                  {jobStatus.curate_summary.action_summary && (
                    <span className={mutedCls}> — {Object.entries(jobStatus.curate_summary.action_summary).map(([k, v]) => `${k}: ${v}`).join(', ')}</span>
                  )}
                </div>
              )}
              {jobStatus.evolve_summary && (
                <div className={`text-xs p-2 rounded ${isDark ? 'bg-slate-700' : 'bg-purple-50'}`}>
                  <span className="font-medium">Evolution:</span>{' '}
                  {jobStatus.evolve_summary.total_proposals} Verbesserungsvorschläge
                  <span className={mutedCls}> ({jobStatus.evolve_summary.eval_reports_analyzed} Eval-Reports analysiert)</span>
                </div>
              )}
            </div>
          )}

          <Separator className={isDark ? 'bg-slate-700' : ''} />

          {/* Available reports */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className={textCls}>Verfügbare Reports</Label>
              <Button variant="ghost" size="sm" onClick={fetchReports} disabled={reportsLoading}>
                <RefreshCw className={`w-4 h-4 mr-1 ${reportsLoading ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
            </div>

            {availableReports.length === 0 && (
              <p className={`text-sm ${mutedCls}`}>Keine Reports vorhanden.</p>
            )}

            <ScrollArea className="max-h-[200px]">
              <div className="space-y-1">
                {availableReports.map((r, i) => (
                  <div
                    key={i}
                    className={`flex items-center justify-between p-2 rounded text-sm ${isDark ? 'hover:bg-slate-800' : 'hover:bg-slate-50'}`}
                  >
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className={
                        r.type === 'judge' ? 'bg-amber-50 text-amber-700 border-amber-300'
                        : r.type === 'curate' ? 'bg-orange-50 text-orange-700 border-orange-300'
                        : r.type === 'evolve' ? 'bg-purple-50 text-purple-700 border-purple-300'
                        : 'bg-sky-50 text-sky-700 border-sky-300'
                      }>
                        {r.type === 'judge' ? 'Judge' : r.type === 'curate' ? 'Kuratierung' : r.type === 'evolve' ? 'Evolution' : 'Eval'}
                      </Badge>
                      <span className={mutedCls}>{r.timestamp}</span>
                      {r.suite && <span className={`text-xs ${mutedCls}`}>({r.suite})</span>}
                    </div>
                    <div className="flex items-center gap-1">
                      {r.type === 'eval' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => loadReport(r.filename_json)}
                          className="text-xs"
                        >
                          Laden
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => downloadReport(r.filename_json)}
                        title="JSON herunterladen"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      {r.filename_csv && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => downloadReport(r.filename_csv)}
                          title="CSV herunterladen"
                        >
                          CSV
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </CardContent>
      </Card>

      {/* Loading overlay */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-sky-500" />
          <span className={`ml-2 ${mutedCls}`}>Report wird geladen...</span>
        </div>
      )}
    </>
  );
}
